/* ── "a guided tour is still running" ─────────────────────────────────
   A host starts a tour for one project, then opens another project to set
   up the next one — and nothing in the product tells them the first tour
   is still live with buyers sitting in it. The room does not end when the
   tab leaves it, so this says so.

   Shaped like an ongoing-call pill rather than a banner: present in the
   corner of every authenticated page, one tap back into the room, and
   small enough to ignore while you work.

   ── WHY IT ASKS THE SERVER ──
   The host's own credential is left in localStorage per room, which is
   enough to know a room was opened from this browser — and not enough to
   know it is still live. A tour someone ended from another device, or
   last week, would otherwise leave a pill claiming buyers are waiting.
   So every candidate is confirmed against the room itself, and a room
   that is gone or ENDED takes its stale key with it. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { findLiveHostedRooms } from "rc-app/utils/guidedRoomLiveHosted";
import { guidedTourEnabled } from "rc-app/utils/featureFlags";
import "./GuidedTourRunning.css";

type Running = { roomId: string; project: string };

/* Re-checked on navigation, and on a slow timer for the case where the
   host leaves the page open: a tour ended elsewhere should stop claiming
   to be live within a minute, not whenever they next click something. */
const RECHECK_MS = 60000;

export const GuidedTourRunning: React.FC = () => {
  const [running, setRunning] = useState<Running[]>([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (checking.current) return;
    checking.current = true;
    try {
      /* A SCHEDULED room blocks a new one but is not a tour happening now,
         and this pill's whole claim is that one IS. Saying "running" about
         a room nobody is in would be the kind of small lie that makes a
         host stop believing the rest of it. */
      const live = (await findLiveHostedRooms()).filter(
        (r) => r.status === "LIVE" || r.status === "LOBBY_OPEN",
      );
      setRunning(live.map((r) => ({ roomId: r.room_id, project: r.project_name })));
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    if (!guidedTourEnabled()) return undefined;
    void check();

    /* The room runs in ANOTHER tab, so coming back to this one is the
       moment the answer most often changed — the host has just ended the
       tour. Waiting out the timer leaves a pill insisting a tour is
       running over a page whose own button has already gone gold, which
       reads as the product disagreeing with itself.

       `storage` covers the same event from the other direction: ending a
       room releases its credential, and that fires here immediately. */
    const recheck = () => { if (document.visibilityState === "visible") void check(); };
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key.startsWith("guidedRoom:")) void check();
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("storage", onStorage);

    const t = setInterval(() => { void check(); }, RECHECK_MS);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("storage", onStorage);
    };
  }, [check, pathname]);

  if (!guidedTourEnabled() || !running.length) return null;

  /* Already in one of them: the room says everything this pill would. */
  if (running.some((r) => pathname.startsWith(`/room/${r.roomId}`))) return null;

  const first = running[0];
  const more = running.length - 1;

  return (
    <button
      type="button"
      className="gtr-pill"
      onClick={() => navigate(`/room/${first.roomId}`)}
      title={first.project ? `Guided tour running · ${first.project}` : "Guided tour running"}
    >
      <span className="gtr-dot" aria-hidden />
      <span className="gtr-text">
        <b>Guided tour running</b>
        {first.project ? <small>{first.project}</small> : null}
      </span>
      {more > 0 ? <span className="gtr-more">+{more}</span> : null}
      <span className="gtr-go">Back to the room</span>
    </button>
  );
};

export default GuidedTourRunning;
